import { useEffect, useMemo, useRef } from 'react';

// Sparkles strip (visual language of 21st.dev @aceternity/sparkles — density
// ~hundreds, 0.4–1.4px particles, transparent bg, soft radial mask — rebuilt
// as a dependency-free canvas so nothing new ships in the supply chain).
// Particles twinkle in/out with a slight upward drift; edges fade so the
// strip has no hard border. Skipped entirely under reduced motion.
export function Sparkles({ density = 320, colors = ['#ffffff', '#ffe5bd', '#ffc46b'], className = '' }) {
  const canvasRef = useRef(null);

  const enabled = useMemo(
    () => typeof window !== 'undefined' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    let w = 0;
    let h = 0;
    let raf = 0;
    let particles = [];
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    function spawn() {
      return {
        x: Math.random(),
        y: Math.random(),
        r: 0.4 + Math.random(), // 0.4–1.4 css px
        color: colors[(Math.random() * colors.length) | 0],
        phase: Math.random() * Math.PI * 2,
        speed: 0.9 + Math.random() * 1.6, // twinkle rate
        drift: 2 + Math.random() * 5, // px/s upward
        born: performance.now() - Math.random() * 4000,
      };
    }

    function resize() {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = Math.max(1, rect.width);
      h = Math.max(1, rect.height);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      particles = Array.from({ length: density }, spawn);
    }

    let inView = true;
    const io = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      if (inView && !raf) raf = requestAnimationFrame(frame);
    });
    io.observe(canvas);

    function frame(now) {
      if (!inView) {
        raf = 0;
        return;
      }
      ctx.clearRect(0, 0, w, h);
      for (const p of particles) {
        const age = (now - p.born) / 1000;
        const twinkle = 0.5 + 0.5 * Math.sin(p.phase + age * p.speed * 2);
        const y = (p.y * h - age * p.drift) % h;
        const yy = y < 0 ? y + h : y;
        const px = p.x * w;
        // soft mask: fade toward left/right edges and toward the bottom
        const edge =
          Math.min(1, (px / w) * 5, ((w - px) / w) * 5) *
          Math.min(1, ((h - yy) / h) * 2.4) *
          Math.min(1, (yy / h) * 6);
        const alpha = twinkle * twinkle * edge;
        if (alpha < 0.02) continue;
        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(px, yy, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(frame);
    }

    resize();
    window.addEventListener('resize', resize);
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, [enabled, density, colors]);

  if (!enabled) return null;

  return <canvas ref={canvasRef} className={`sparkles-canvas ${className}`} aria-hidden="true" />;
}
